import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { keys as getIDBKeyValKeys, del as delIDBKeyVal } from 'idb-keyval'
import { captureException, withScope } from '@sentry/react'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { useQueryClient } from '@tanstack/react-query'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useDeleteMyPushDevice } from '@/service-library/hooks/notifications'
import { IDB_VALID_KEY_PREFIX } from '@/utils/persister'
import {
  PUSH_TOKEN_STORAGE_KEY,
  revokePushToken,
  waitForPushRegistration,
} from '@/utils/push-notifications'

function report(err: unknown, step: string) {
  withScope((scope) => {
    scope.setTag('sign_out_step', step)
    captureException(err)
  })
}

export default function useSignOut() {
  const queryClient = useQueryClient()
  const stytch = useStytchB2BClient()
  const navigate = useNavigate()
  const { deleteMyPushDeviceAsync } = useDeleteMyPushDevice()
  const [storedPushToken, setStoredPushToken] = useLocalStorage<string | null>(
    PUSH_TOKEN_STORAGE_KEY,
    null,
    false,
  )

  // Never throws. Callers fire this from a click handler without waiting on
  // it, so anything thrown would become an unhandled rejection.
  const signOut = useCallback(async () => {
    // This needs the session, so it runs before the session is revoked.
    // Without it, this browser keeps getting the user's pushes after sign out.
    // A registration still in flight is waited for first, or it could recreate
    // the row after it's deleted. Its token is newer than the stored one.
    const pushToken = (await waitForPushRegistration()) ?? storedPushToken
    if (pushToken) {
      let isRemoved = false
      try {
        await deleteMyPushDeviceAsync({
          device_platform: 'GCM',
          device_token: pushToken,
        })
        isRemoved = true
      } catch (err) {
        report(err, 'delete-push-device')
        // The row is still there, so kill the token instead. FCM then rejects
        // pushes sent to it and pd-notify marks the device invalid.
        try {
          isRemoved = await revokePushToken()
        } catch (revokeErr) {
          report(revokeErr, 'revoke-push-token')
        }
      }
      // Kept when both failed, so the next sign-out from here can try again.
      if (isRemoved) setStoredPushToken(null)
    }

    queryClient.clear()

    // If clearing the cache fails, the sign out below must still happen, or
    // the session stays alive while the user believes they signed out.
    try {
      const allKeys = await getIDBKeyValKeys()
      const tanstackQueryKeys = allKeys.filter(
        (key) =>
          typeof key === 'string' && key.startsWith(IDB_VALID_KEY_PREFIX),
      )
      await Promise.all(tanstackQueryKeys.map((key) => delIDBKeyVal(key)))
    } catch (err) {
      report(err, 'clear-idb')
    }

    try {
      await stytch.session.revoke()
    } catch (err) {
      report(err, 'revoke-session')
    }

    navigate('/')
  }, [
    deleteMyPushDeviceAsync,
    navigate,
    queryClient,
    setStoredPushToken,
    storedPushToken,
    stytch.session,
  ])

  return signOut
}
